import { API_PREFIX, type ErrorDto } from "@qa/shared";

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
    /** Chiave del messaggio (catalogo MESSAGGI di @qa/shared), tradotta da i18n/errori.ts. */
    public key?: string,
    public params?: Record<string, string | number>,
  ) {
    super(message);
  }
}

type Query = Record<string, string | number | boolean | null | undefined>;
const qs = (q?: Query) => {
  if (!q) return "";
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== null && v !== "") p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : "";
};

/** URL assoluto di una rotta API (per download diretti: xlsx, pdf, evidenze). */
export const apiUrl = (path: string, q?: Query) => `${API_PREFIX}${path}${qs(q)}`;

async function request<T>(method: string, path: string, body?: unknown, q?: Query): Promise<T> {
  const init: RequestInit = { method, credentials: "same-origin", headers: {} };
  if (body instanceof FormData) init.body = body;
  else if (body !== undefined) {
    init.body = JSON.stringify(body);
    (init.headers as Record<string, string>)["content-type"] = "application/json";
  }
  const res = await fetch(apiUrl(path, q), init);
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get("content-type") || "";
  const data = ct.includes("application/json") ? await res.json() : await res.text();
  if (!res.ok) {
    const e = (data as ErrorDto)?.error;
    throw new ApiError(res.status, e?.code ?? "error", e?.message ?? res.statusText, e?.details, e?.key, e?.params);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string, q?: Query) => request<T>("GET", path, undefined, q),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body ?? {}),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body ?? {}),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body ?? {}),
  del: <T>(path: string) => request<T>("DELETE", path),
};

/** Scarica un file da una rotta GET (xlsx/pdf) senza lasciare la pagina. */
export function download(path: string, q?: Query) {
  const a = document.createElement("a");
  a.href = apiUrl(path, q);
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}
